import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Heading,
  Label,
  Modal,
  ModalOverlay,
  Radio,
  RadioGroup,
  Switch,
} from 'react-aria-components';
import {
  useAccessibilityPreferences,
  type ColorMode,
  type TextScale,
} from '../accessibility/AccessibilityPreferences';

export function AccessibilityDialog() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  const {
    colorMode,
    textScale,
    reduceMotion,
    setColorMode,
    setTextScale,
    setReduceMotion,
    reset,
  } = useAccessibilityPreferences();

  const showDialog = () => {
    const activeElement = document.activeElement;
    originRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : triggerRef.current;
    setOpen(true);
  };

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      const destination = originRef.current ?? triggerRef.current;
      originRef.current = null;
      window.setTimeout(() => destination?.focus(), 0);
    }
  };

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (
        event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        event.key.toLocaleLowerCase('es') === 'a'
      ) {
        event.preventDefault();
        if (open) changeOpen(false);
        else showDialog();
      }
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [open]);

  return (
    <>
      <Button
        ref={triggerRef}
        className="accessibility-trigger"
        aria-keyshortcuts="Alt+A"
        onPress={showDialog}
      >
        <span className="accessibility-short" aria-hidden="true">A</span>
        <span className="accessibility-label">Accesibilidad</span>
      </Button>
      <ModalOverlay
        className="modal-overlay"
        isOpen={open}
        onOpenChange={changeOpen}
        isDismissable
      >
        <Modal className="accessibility-modal">
          <Dialog aria-label="Opciones de accesibilidad" className="accessibility-dialog">
            {() => (
              <>
                <div className="dialog-heading">
                  <Heading slot="title">Opciones de accesibilidad</Heading>
                  <Button
                    className="icon-button"
                    aria-label="Cerrar opciones de accesibilidad"
                    onPress={() => changeOpen(false)}
                  >
                    <span aria-hidden="true">×</span>
                  </Button>
                </div>

                <RadioGroup
                  className="preference-group"
                  value={colorMode}
                  onChange={(value) => setColorMode(value as ColorMode)}
                >
                  <Label>Presentación de colores</Label>
                  <Radio value="system">Usar configuración del sistema</Radio>
                  <Radio value="light">Tema claro</Radio>
                  <Radio value="dark">Tema oscuro</Radio>
                  <Radio value="contrast">Contraste alto</Radio>
                </RadioGroup>

                <RadioGroup
                  className="preference-group"
                  value={textScale}
                  onChange={(value) => setTextScale(value as TextScale)}
                >
                  <Label>Tamaño del texto</Label>
                  <Radio value="100">Normal</Radio>
                  <Radio value="112">Grande</Radio>
                  <Radio value="125">Muy grande</Radio>
                </RadioGroup>

                <Switch
                  className="motion-switch"
                  isSelected={reduceMotion}
                  onChange={setReduceMotion}
                >
                  <span className="switch-control" aria-hidden="true">
                    <span className="switch-indicator" />
                  </span>
                  Reducir animaciones
                </Switch>

                <div className="dialog-actions">
                  <Button className="secondary-button" onPress={reset}>
                    Restablecer
                  </Button>
                  <Button className="primary-button" onPress={() => changeOpen(false)}>
                    Guardar y cerrar
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
}
